import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../services/AuthContext";
import { 
  Container, 
  Header, 
  Logo, 
  Nav, 
  Hamburger,
  MobileMenuOverlay,
  MobileMenu,
  GrabberHandle,
  MobileMenuLogo,
  Divider,
  MenuLink,
  Main, 
  LoginBox, 
  Title, 
  InputField, 
  LoginButton, 
  LinkText, 
  ForgotPasswordLink,
  Footer,
  ErrorMessage
} from "../styles/LoginStyles";
import bgImage from '../assets/bgimagefinal.png';
import logo from '../assets/logo-white.png';

function Login() {
  const { login } = useAuthContext();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const handleLogin = async () => {
    setIsLoading(true);
    setError("");
    try {
      await login(email, password);
      const role = localStorage.getItem("role") || "user";
      alert(role === "admin" ? "Welcome Admin!" : "Login successful!");
      navigate(role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(err.message || "Invalid credentials!");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const handleLinkClick = (e, path) => {
    const link = e.currentTarget;
    const ripple = document.createElement("span");
    ripple.classList.add("ripple");
    const rect = link.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
    link.appendChild(ripple);

    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }

    setTimeout(() => {
      closeMenu();
      navigate(path);
    }, 200);
  };

  const getLinkClass = (path) => {
    return location.pathname === path ? "active" : "";
  };

  return (
    <Container bgImage={bgImage}>
      <Header>
        <Logo src={logo} alt="Nutrify Today Logo" />
        <Nav>
          <a href="/">HOME</a>
          <a href="/about">ABOUT</a>
          <a href="/contact">CONTACT</a>
          <a href="/login">LOGIN</a>
          <a href="/signup" className="register">REGISTER</a>
        </Nav>
        <Hamburger 
          className={menuOpen ? 'open' : ''} 
          onClick={toggleMenu}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && toggleMenu()}
        >
          <span></span>
          <span></span>
          <span></span>
        </Hamburger>
      </Header>
      <Main>
        <LoginBox>
          <Title>Login</Title>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <InputField
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
          <InputField
            type="password"
            placeholder="********"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
          <LoginButton onClick={handleLogin} disabled={isLoading}>
            {isLoading ? "Logging in..." : "LOGIN"}
          </LoginButton>
          <LinkText>
            Don’t have an account? <a href="/signup">Register</a>
          </LinkText>
          <LinkText className="forgot-password-desktop">
            Forgot password? <a href="/forgotpassword">Reset</a>
          </LinkText>
        </LoginBox>
        
        <MobileMenuOverlay isOpen={menuOpen} onClick={closeMenu} />
        <MobileMenu isOpen={menuOpen} ref={menuRef}>
          <MobileMenuLogo src={logo} alt="Nutrify Today Logo" />
          <Divider />
          <MenuLink 
            href="/" 
            className={getLinkClass("/")} 
            onClick={(e) => handleLinkClick(e, "/")} 
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/")}
          >
            HOME
          </MenuLink>
          <MenuLink 
            href="/about" 
            className={getLinkClass("/about")} 
            onClick={(e) => handleLinkClick(e, "/about")} 
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/about")}
          >
            ABOUT
          </MenuLink>
          <MenuLink 
            href="/contact" 
            className={getLinkClass("/contact")} 
            onClick={(e) => handleLinkClick(e, "/contact")} 
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/contact")}
          >
            CONTACT
          </MenuLink>
          <MenuLink 
            href="/login" 
            className={getLinkClass("/login")} 
            onClick={(e) => handleLinkClick(e, "/login")} 
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/login")}
          >
            LOGIN
          </MenuLink>
          <MenuLink 
            href="/signup" 
            className={`register ${getLinkClass("/signup")}`} 
            onClick={(e) => handleLinkClick(e, "/signup")} 
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/signup")}
          >
            REGISTER
          </MenuLink>
          <GrabberHandle 
            onClick={closeMenu}
            aria-label="Close menu"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && closeMenu()}
          />
        </MobileMenu>
      </Main>
      <Footer>
        <p>© 2025 DealSphere. All rights reserved.</p>
        <div>
          <a href="#terms">Terms</a>
          <a href="#privacy">Privacy</a>
          <a href="#contact">Contact</a>
        </div>
      </Footer>
    </Container>
  );
}

export default Login;